import { useCallback, useRef, useState, DragEvent, ChangeEvent } from 'react';
import {
  Modal,
  Box,
  Typography,
  Backdrop,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';

import { useTextStyles } from 'src/hooks/useTextStyles';
import useNotification from 'src/hooks/useNotification';
import useDashboardFilters from 'src/hooks/useDashboardFilters';

import {
  canonicalizeImportedRows,
  parseImportFileRows,
  processPreferenceCsvImport,
} from './preferenceShowCsvPipeline';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 560,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      marginBottom: 12,
    },
    dragWrap: {
      position: 'relative',
      '& > input[type="file"]': { display: 'none' },
      '& label': {
        display: 'flex',
        border: '1px dashed #EAECF0',
        borderRadius: 8,
        padding: '32px 20px',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'background 0.2s ease',
        flexDirection: 'column',
        minHeight: 120,
      },
      '& label.drag-active': {
        background: '#F9FAFB',
        borderColor: '#5EA1F2',
      },
    },
    loadingOverlay: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 8,
      background: 'rgba(255,255,255,0.85)',
      borderRadius: 8,
      zIndex: 4,
    },
  })
);

interface PreferenceShowCsvImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: (showNames: string[], warnings?: string[]) => void;
  title?: string;
}

export const PreferenceShowCsvImportModal = ({
  open,
  onClose,
  onImported,
  title = 'Import show names',
}: PreferenceShowCsvImportModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();
  const { handleError } = useNotification();
  const { customHeaders } = useDashboardFilters();

  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetAndClose = useCallback(() => {
    setLoading(false);
    setDragActive(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onClose();
  }, [onClose]);

  const handleProcessFile = async (file: File) => {
    setLoading(true);
    try {
      const rows = await parseImportFileRows(file);
      const mapped = canonicalizeImportedRows(rows, {
        isNoRSSFeed: false,
        customHeaders,
      });

      const { showNames, warnings } = await processPreferenceCsvImport({
        mappedData: mapped,
        isNoRSSFeed: false,
        customHeaders,
      });

      if (!showNames.length) {
        handleError({}, 'No Barometer matches were returned from this file.');
        resetAndClose();
        return;
      }

      onImported(showNames, warnings);
      resetAndClose();
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : 'Failed to import show names.';
      handleError(e, msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!loading && e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleProcessFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!loading && e.target.files && e.target.files[0]) {
      handleProcessFile(e.target.files[0]);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => (!loading ? resetAndClose() : undefined)}
      aria-labelledby='preference-csv-import-title'
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Box sx={{ ...modalStyle, position: 'relative' }}>
        <Box className={classes.header}>
          <Typography
            id='preference-csv-import-title'
            className={textClasses.lgBold}
            style={{ color: '#101828' }}
          >
            {title}
          </Typography>
          <CancelIcon
            cursor={loading ? 'default' : 'pointer'}
            style={{ opacity: loading ? 0.4 : 1 }}
            onClick={() => !loading && resetAndClose()}
          />
        </Box>
        <Box
          className={classes.dragWrap}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={inputRef}
            type='file'
            accept='.csv,.xls,.xlsx'
            disabled={loading}
            onChange={handleChange}
            id='preference-csv-file'
          />
          <label
            htmlFor='preference-csv-file'
            className={dragActive ? 'drag-active' : undefined}
          >
            <Typography
              className={textClasses.smBold}
              style={{ color: '#344054', textAlign: 'center' }}
            >
              Drag & drop CSV or Excel here or click to browse
            </Typography>
            <Typography
              className={textClasses.smRegular}
              style={{ color: '#98A2B3', marginTop: 6, textAlign: 'center' }}
            >
              Dashboard import pipeline • max 1 MB
            </Typography>
          </label>
          {loading && (
            <Box className={classes.loadingOverlay}>
              <CircularProgress style={{ color: '#5EA1F2' }} size={36} />
              <Typography
                className={textClasses.smBold}
                style={{ color: '#475467' }}
              >
                Matching shows…
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
